import { type BacklogHorizon, TIME_HORIZON_ORDER } from "@pilotfish/core";

/**
 * **What a family calls each planning bucket** — the three headings on the list screen
 * (issue #310, `Backlog.dc.html`).
 *
 * ## Why the words are here and the order is not
 *
 * `TIME_HORIZON_ORDER` is `@pilotfish/core`'s, because `get_backlog` groups by it too and its
 * acceptance criterion is *the tool's own order*. These **words** are not, and the split is
 * `words.ts`'s rule applied honestly: core holds *words more than one runtime must agree on*,
 * and no tool says any of these to a family. `get_backlog` returns `groups` keyed by the raw
 * `today | this_week | later` and leaves the phrasing to whatever is reading them out; its
 * description glosses the enum for a host as *"today / this week / later"*, which is the
 * schema explained to a model rather than a heading anybody reads. So one screen says these,
 * and one screen owns them — the day a tool speaks them, they move, the way
 * `CONTEXT_KIND_HEADING` already did.
 *
 * ## The headings are the canvas's
 *
 * *This week* and *Soon* are read off `Backlog.dc.html`. **`later` → *Soon*, not *Later*** is
 * the one worth stating: *later* is the schema's word for the far bucket and it reads as a
 * shrug — a place things go to be forgotten — where the canvas's *Soon* says the family still
 * means to do it. That is the difference between a backlog and a list somebody keeps.
 *
 * ## A horizon this build has never heard of is shown, not swallowed
 *
 * `timeHorizon` arrives as a plain string. A fourth bucket added server-side reaches an older
 * bundle with no heading for it, and dropping those items would make a screen that puts a
 * count beside every heading quietly wrong — the "inventory lies" failure the export's own
 * design refuses. So {@link isHorizon} narrows, and `Plate.tsx` groups the rest under their
 * raw value where they stay readable, correctable and deletable.
 */

export const HORIZON_HEADING: Readonly<Record<BacklogHorizon, string>> = {
  today: "Today",
  this_week: "This week",
  later: "Soon",
};

export function isHorizon(value: string): value is BacklogHorizon {
  return (TIME_HORIZON_ORDER as readonly string[]).includes(value);
}

/**
 * The buckets as a picker offers them — the same three, in the same order, with their headings.
 *
 * Derived rather than written out a second time, so *When?* on the edit form and the heading
 * above the row it sits in can never disagree.
 */
export const HORIZON_CHOICE: readonly { readonly value: BacklogHorizon; readonly label: string }[] =
  TIME_HORIZON_ORDER.map((value) => ({ value, label: HORIZON_HEADING[value] }));
